import { jsxs, jsx } from "react/jsx-runtime";
import assert from "assert";
import { useContext, useRef, useEffect } from "react";
import { atoms } from "../../css/atoms/atoms.mjs";
import { iconSize } from "../../hooks/useIcon/index.mjs";
import { Box } from "../Box/Box.mjs";
import { useBraidTheme } from "../BraidProvider/BraidThemeContext.mjs";
import { actionTypes } from "../MenuRenderer/MenuRenderer.actions.mjs";
import { MenuRendererContext } from "../MenuRenderer/MenuRendererContext.mjs";
import { MenuRendererItemContext } from "../MenuRenderer/MenuRendererItemContext.mjs";
import { Text } from "../Text/Text.mjs";
import { badgeSlotSpace } from "../private/badgeSlotSpace.mjs";
import { buildDataAttributes } from "../private/buildDataAttributes.mjs";
import { iconSlotSpace } from "../private/iconSlotSpace.mjs";
import { normalizeKey } from "../private/normalizeKey.mjs";
import { menuItem, menuItemLeftSlot } from "./useMenuItem.css.mjs";
import { virtualTouchable } from "../private/touchable/virtualTouchable.css.mjs";
const {
  MENU_ITEM_UP,
  MENU_ITEM_DOWN,
  MENU_ITEM_ENTER,
  MENU_ITEM_SPACE,
  MENU_ITEM_CLICK,
  MENU_ITEM_HOVER
} = actionTypes;
function useMenuItem({
  displayName = "MenuItem",
  formElement = false,
  tone,
  onClick,
  data,
  id,
  ...restProps
}) {
  const menuRendererContext = useContext(MenuRendererContext);
  const menuRendererItemContext = useContext(MenuRendererItemContext);
  assert(
    menuRendererContext !== null && menuRendererItemContext !== null,
    `${displayName} must be rendered as an immediate child of a menu. See the documentation for correct usage: https://seek-oss.github.io/braid-design-system/components/MenuItem`
  );
  if (menuRendererItemContext === null) {
    throw new Error(`${displayName} element rendered outside menu context`);
  }
  const { size } = menuRendererContext;
  const { isHighlighted, index, dispatch, focusTrigger } = menuRendererItemContext;
  const menuItemRef = useRef(null);
  useEffect(() => {
    var _a;
    if (isHighlighted) {
      (_a = menuItemRef.current) == null ? void 0 : _a.focus();
    }
  }, [isHighlighted]);
  const onKeyDown = (event) => {
    var _a;
    const targetKey = normalizeKey(event);
    const closeActionKeys = ["Enter", " "];
    const isArrowPress = targetKey.indexOf("Arrow") === 0;
    const isActionKeyPress = targetKey === "Enter" || targetKey === " ";
    if (isArrowPress || isActionKeyPress) {
      event.preventDefault();
    }
    const action = {
      ArrowDown: { type: MENU_ITEM_DOWN },
      ArrowUp: { type: MENU_ITEM_UP },
      Enter: { type: MENU_ITEM_ENTER, formElement, index, id },
      " ": { type: MENU_ITEM_SPACE, formElement, index, id }
    };
    if (action[targetKey]) {
      dispatch(action[targetKey]);
    }
    if (targetKey === "Enter" || targetKey === " ") {
      (_a = menuItemRef.current) == null ? void 0 : _a.click();
    }
    if (!formElement && closeActionKeys.indexOf(targetKey) > -1) {
      focusTrigger();
    }
  };
  const hoverBackground = tone === "critical" ? "criticalLight" : "formAccentSoft";
  return {
    MenuItemChildren,
    menuItemProps: {
      role: "menuitem",
      tabIndex: -1,
      ref: menuItemRef,
      id,
      onKeyDown,
      onMouseEnter: () => dispatch({ type: MENU_ITEM_HOVER, value: index }),
      onClick: (event) => {
        event.stopPropagation();
        dispatch({ type: MENU_ITEM_CLICK, formElement, index, id });
        if (typeof onClick === "function") {
          onClick();
        }
      },
      /*
      On mobile, switching between 'undefined' and a specified background converts the element to a `ColoredBox` on touch.
      This can interrupt href navigation and click events on iOS.
      */
      background: isHighlighted ? hoverBackground : "surface",
      className: [
        menuItem,
        size === "small" ? virtualTouchable : void 0,
        atoms({
          display: "flex",
          alignItems: "center",
          width: "full",
          paddingX: "small",
          paddingY: size === "standard" ? void 0 : "xsmall",
          height: size === "standard" ? "touchable" : void 0,
          cursor: "pointer",
          textAlign: "left",
          outline: "none"
        })
      ],
      ...buildDataAttributes({ data, validateRestProps: restProps })
    }
  };
}
function MenuItemChildren({
  leftSlot,
  tone,
  children,
  badge,
  isCheckbox = false
}) {
  const menuRendererContext = useContext(MenuRendererContext);
  const badgeSpace = useBraidTheme().legacy ? "small" : badgeSlotSpace;
  const iconSpace = useBraidTheme().legacy ? "small" : iconSlotSpace;
  assert(
    menuRendererContext !== null,
    `MenuItem must be rendered as an immediate child of a menu. See the documentation for correct usage: https://seek-oss.github.io/braid-design-system/components/MenuItem`
  );
  assert(
    // @ts-expect-error
    !badge || badge.type.__isBadge__,
    `MenuItem badge prop can only be an instance of Badge. e.g. <MenuItem badge={<Badge>New</Badge>}>`
  );
  const { size, reserveIconSpace } = menuRendererContext;
  return /* @__PURE__ */ jsxs(Box, { component: "span", display: "flex", alignItems: "center", minWidth: 0, children: [
    leftSlot || reserveIconSpace ? /* @__PURE__ */ jsxs(
      Box,
      {
        component: "span",
        position: "relative",
        display: "flex",
        alignItems: "center",
        paddingRight: iconSpace,
        children: [
          /* @__PURE__ */ jsx(
            Box,
            {
              component: "span",
              display: "block",
              className: [iconSize({ size }), menuItemLeftSlot],
              children: " "
            }
          ),
          leftSlot ? /* @__PURE__ */ jsx(Box, { component: "span", position: "absolute", children: isCheckbox ? leftSlot : /* @__PURE__ */ jsx(
            Text,
            {
              size,
              tone: tone && tone === "critical" ? tone : void 0,
              children: leftSlot
            }
          ) }) : null
        ]
      }
    ) : null,
    /* @__PURE__ */ jsx(Box, { component: "span", minWidth: 0, children: /* @__PURE__ */ jsx(
      Text,
      {
        size,
        tone: tone === "critical" ? tone : void 0,
        maxLines: 1,
        children
      }
    ) }),
    badge ? /* @__PURE__ */ jsx(Box, { component: "span", paddingLeft: badgeSpace, children: /* @__PURE__ */ jsx(Text, { size, children: badge }) }) : null
  ] });
}
export {
  useMenuItem
};
